/**
 * Camera Shake Effect
 * 
 * Calculates camera shake offsets to be applied by CameraController.
 * Supports multiple simultaneous shakes with additive blending.
 * Uses real time (performance.now) so shake runs at full speed during slow-mo.
 */

import { Vector3 } from '@babylonjs/core';
import { logger } from '../../core/Logger';
import { resolveTimeRamp } from './TimeRamps';
import type { TimeRamp, TimeRampPreset } from './TimeRamps';

/**
 * Configuration for a camera shake effect
 */
export interface ShakeEffectConfig {
  /** Duration in seconds (default: 0.3) */
  duration?: number;
  /** Maximum shake offset in world units (default: 0.15) */
  amplitude?: number;
  /** Shake frequency in Hz (default: 30) */
  frequency?: number;
  /** Time ramp preset or custom function (default: 'easeOut') */
  timeRamp?: TimeRamp | TimeRampPreset;
}

interface ActiveShake {
  id: string;
  startTime: number;
  durationMs: number;
  amplitude: number;
  frequency: number;
  timeRamp: TimeRamp;
}

const DEFAULT_CONFIG = {
  duration: 0.3,
  amplitude: 0.15,
  frequency: 30,
};

let shakeIdCounter = 0;

export class CameraShakeEffect {
  private activeShakes: Map<string, ActiveShake> = new Map();
  
  // Current computed offsets (updated each frame)
  private positionOffset: Vector3 = Vector3.Zero();
  private targetOffset: Vector3 = Vector3.Zero();

  constructor() {
    logger.debug.log('✅ CameraShakeEffect initialized');
  }

  /**
   * Trigger a camera shake effect
   * @returns Effect ID for potential cancellation
   */
  public trigger(config: ShakeEffectConfig = {}): string {
    const id = `shake_${++shakeIdCounter}_${Date.now()}`;
    
    const shake: ActiveShake = {
      id,
      startTime: performance.now(),
      durationMs: (config.duration ?? DEFAULT_CONFIG.duration) * 1000,
      amplitude: config.amplitude ?? DEFAULT_CONFIG.amplitude,
      frequency: config.frequency ?? DEFAULT_CONFIG.frequency,
      timeRamp: resolveTimeRamp(config.timeRamp ?? 'easeOut'),
    };
    
    this.activeShakes.set(id, shake);
    
    logger.debug.debug(`📳 Shake started: ${id}, amplitude=${shake.amplitude.toFixed(3)}`);
    
    return id;
  }

  /**
   * Cancel a specific shake effect
   */
  public cancel(id: string): void {
    this.activeShakes.delete(id);
  }

  /**
   * Cancel all active shakes
   */
  public cancelAll(): void {
    this.activeShakes.clear();
    this.positionOffset.set(0, 0, 0);
    this.targetOffset.set(0, 0, 0);
  }

  /**
   * Update all active shakes and compute combined offset.
   * Call every frame. Uses performance.now() for real-time.
   */
  public update(_deltaTime: number): void {
    const now = performance.now();
    const completedShakes: string[] = [];
    
    // Accumulate offsets from all active shakes
    let totalX = 0;
    let totalY = 0;
    let totalZ = 0;
    
    for (const [id, shake] of this.activeShakes) {
      const elapsed = now - shake.startTime;
      const progress = Math.min(1, elapsed / shake.durationMs);
      
      if (progress >= 1) {
        completedShakes.push(id);
        continue;
      }
      
      const intensity = shake.timeRamp(progress);
      const time = elapsed / 1000;
      const freq = shake.frequency;
      
      // Use multiple sine wave frequencies for organic shake
      const shakeX = Math.sin(time * freq * 2 * Math.PI) * 0.7 +
                     Math.sin(time * freq * 1.3 * 2 * Math.PI) * 0.3;
      const shakeY = Math.cos(time * freq * 2 * Math.PI) * 0.6 +
                     Math.cos(time * freq * 1.7 * 2 * Math.PI) * 0.4;
      const shakeZ = Math.sin(time * freq * 0.7 * 2 * Math.PI) * 0.3;
      
      totalX += shakeX * shake.amplitude * intensity;
      totalY += shakeY * shake.amplitude * intensity;
      totalZ += shakeZ * shake.amplitude * intensity * 0.5;
    }
    
    // Store computed offsets
    this.positionOffset.set(totalX, totalY, totalZ);
    // Target offset is the same for now (camera shakes in place)
    this.targetOffset.set(totalX, totalY, totalZ);
    
    // Clean up completed shakes
    for (const id of completedShakes) {
      this.activeShakes.delete(id);
    }
  }

  /**
   * Get the current camera position offset to apply
   */
  public getPositionOffset(): Vector3 {
    return this.positionOffset;
  }

  /**
   * Get the current camera target offset to apply
   */
  public getTargetOffset(): Vector3 {
    return this.targetOffset;
  }

  /**
   * Check if any shakes are active
   */
  public hasActiveEffects(): boolean {
    return this.activeShakes.size > 0;
  }

  /**
   * Get count of active shakes
   */
  public getActiveCount(): number {
    return this.activeShakes.size;
  }

  /**
   * Dispose resources
   */
  public dispose(): void {
    this.cancelAll();
    logger.debug.log('🗑️ CameraShakeEffect disposed');
  }
}
